import { useAuth0 } from '@auth0/auth0-react';
import VisibilityIcon from '@mui/icons-material/Visibility';
import {
    Modal,
    TableContainer,
    TableRow,
    TableBody,
    TableCell,
    TableHead,
    Typography,
    Grid,
    Table,
    Button,
    IconButton,
    CircularProgress,
    Chip,
} from '@mui/material';
import { styled, Paper } from '@material-ui/core';
import { useState, useEffect } from 'react';
import CloseIcon from '@mui/icons-material/Close';
import axios from 'axios';
import DocumentPreview from '../BookingInfo/DocumentPreview';
import sendNotification from '../../utils/sendNotification';

const ScrollableModal = styled(Modal)(() => ({
    overflow: 'scroll',
}));

const CenterBox = styled('Box')(() => ({
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    boxShadow: 24,
    p: 4,
}));

const SchoolLogoImg = styled('img')(() => ({
    maxWidth: '100%',
    height: 'auto',
    padding: '20px',
}));

const SignedButton = styled(Button)(() => ({
    '&:disabled': {
        backgroundColor: 'green',
        color: 'white',
    },
}));

/*
 * @component SignSchoolDocs
 * @param {callback} props.setOpen - open or close
 * @param {object} props.schoolDocsInfo - all school info
 * @param {string} props.schoolDocsInfo.docs - documents
 * @param {string} props.schoolDocsInfo.logo - school logo
 * @param {string} props.schoolDocsInfo.name - school name
 * @param {callback} props.setSnack
 * @param {callback} props.confirmBooking - rerun after signing
 */
const SignSchoolDocs = props => {
    const { getAccessTokenSilently } = useAuth0();
    const [docs, setDocs] = useState([]);
    const [viewDoc, setViewDoc] = useState({});
    const [current, setCurrent] = useState('');

    useEffect(() => {
        if (props.schoolDocsInfo.docs === undefined) return;
        let interimDocs = [...props.schoolDocsInfo.docs];
        interimDocs.map(item => {
            const match = item.sk1.match(/\d{6}_(.*)/);
            if (match) {
                item.name = match[1];
            }
            item.downloaded = false;
        });
        setDocs(interimDocs);
    }, [props.schoolDocsInfo.docs]);

    useEffect(() => {
        const done =
            docs.length > 0 && docs.filter(doc => !doc.signed).length === 0;
        if (done) {
            sendNotification(
                'success',
                6000,
                'Successfuly signed all documents'
            );
            props.confirmBooking();
            setTimeout(() => {
                props.setOpen(false);
            }, 1000);
        }
    }, [docs]);

    const handleClose = () => props.setOpen(false);

    const getDocById = sk1 => {
        for (const doc of docs) {
            if (doc.sk1 === sk1) {
                return doc;
            }
        }
        console.log('Error: no doc returned');
    };

    // Allows docs to be set asynchronously
    // and independantly
    const setDocById = (sk1, doc) => {
        const interimDocs = [...docs];
        for (let originalDoc of interimDocs) {
            if (originalDoc.sk1 === sk1) {
                originalDoc = doc;
                break;
            }
        }
        setDocs(interimDocs);
    };

    const showItem = async sk1 => {
        if (current === sk1) {
            setCurrent('');
            return;
        }
        setCurrent(sk1);

        const doc = getDocById(sk1);
        const filename = doc.url;
        const type = doc.filetype;
        const token = await getAccessTokenSilently({
            scope: process.env.REACT_APP_scope,
        });
        let url = '/getSignedDocument';

        await axios
            .get(url, {
                baseURL: process.env.REACT_APP_API_GATEWAY,
                url: url,
                timeout: 30000,
                headers: {
                    authorization: `Bearer ${token}`,
                    'Content-Type': 'application/json',
                },
                params: {
                    Filename: filename,
                    Date: doc.date,
                    schoolPk1: doc.pk1,
                    userType: 'teacher',
                },
            })
            .then(output => {
                doc.downloaded = true;
                setDocById(sk1, doc);
                setViewDoc({
                    'type': type,
                    url: output.data.Message.Data.downloadURL,
                });
            })
            .catch(() => {
                setViewDoc({
                    'type': '',
                });
            });
    };

    const handleSign = sk1 => {
        const doc = getDocById(sk1);

        doc.loading = true;
        setDocById(sk1, doc);

        async function signDocument(doc) {
            const token = await getAccessTokenSilently({
                scope: process.env.REACT_APP_scope,
            });
            const body = {
                doc: doc,
            };
            const response = await axios.put('/signSchoolDocument', body, {
                baseURL: process.env.REACT_APP_API_GATEWAY,
                url: '/signSchoolDocument',
                timeout: 30000,
                headers: {
                    authorization: `Bearer ${token}`,
                    'Content-Type': 'application/json',
                },
            });
            console.log('Response', response);
            doc.loading = false;
            if (response.status === 200) {
                doc.signed = true;
            } else {
                sendNotification('error', 0, 'Error signing document');
            }
            setDocById(doc);
        }
        signDocument(doc);
    };

    return (
        <>
            <ScrollableModal
                open={true}
                onClose={handleClose}
                sx={{ 'z-index': 1300 }}
            >
                <CenterBox component={Paper}>
                    <Grid container alignItems="center">
                        <Grid item xs={12}>
                            <IconButton
                                color="secondary"
                                onClick={handleClose}
                                align="left"
                            >
                                <CloseIcon />
                            </IconButton>
                        </Grid>
                        <Grid item xs={12} sm={6} md={4} align="center">
                            <SchoolLogoImg
                                src={props.schoolDocsInfo.logo}
                                alt={`${props.schoolDocsInfo.name} School Logo`}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6} md={8} align="center">
                            <Typography variant="h2" sx={{ fontWeight: '400' }}>
                                {props.schoolDocsInfo.name}
                            </Typography>
                        </Grid>
                        <Grid item sx={12} alignContent="center">
                            <Typography variant="h6" p={4}>
                                All documents must be viewed and signed before
                                accepting the booking
                            </Typography>
                        </Grid>
                        <Grid item xs={12}>
                            <TableContainer>
                                <Table>
                                    <TableHead>
                                        <TableRow>
                                            <TableCell align="center">
                                                Document Name
                                            </TableCell>
                                            <TableCell align="center">
                                                Preview
                                            </TableCell>
                                            <TableCell align="center">
                                                Signed
                                            </TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {docs.map(doc => (
                                            <TableRow key={doc.name}>
                                                <TableCell
                                                    component="th"
                                                    scope="row"
                                                    align="center"
                                                >
                                                    {doc.name}
                                                </TableCell>
                                                <TableCell align="center">
                                                    <Chip
                                                        label="View"
                                                        data-cy="view-doc"
                                                        icon={
                                                            <VisibilityIcon />
                                                        }
                                                        onClick={() => {
                                                            showItem(
                                                                //doc.url,
                                                                //doc.filetype
                                                                doc.sk1
                                                            );
                                                        }}
                                                    />
                                                </TableCell>
                                                <TableCell align="center">
                                                    {doc.signed ? (
                                                        <SignedButton
                                                            variant="outlined"
                                                            disabled
                                                        >
                                                            Signed
                                                        </SignedButton>
                                                    ) : doc.loading ? (
                                                        <CircularProgress />
                                                    ) : (
                                                        <Button
                                                            variant="contained"
                                                            disabled={
                                                                !doc.downloaded
                                                            }
                                                            onClick={() =>
                                                                handleSign(
                                                                    doc.sk1
                                                                )
                                                            }
                                                        >
                                                            Sign
                                                        </Button>
                                                    )}
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        </Grid>
                        {current !== '' && (
                            <DocumentPreview viewDocument={viewDoc} />
                        )}
                    </Grid>
                </CenterBox>
            </ScrollableModal>
        </>
    );
};

export default SignSchoolDocs;
